import moment from 'moment';
import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import {
  ACTIVE_PROFILE,
  API_URL,
  IMAGE_UPLOADER_URL,
  VIDEO_UPLOADER_URL
} from '../config';

import Placeholder1x1 from './assets/image-1x1.png';
import Placeholder4x3 from './assets/image-4x3.png';
import Placeholder16x9 from './assets/image-16x9.png';
import Placeholder9x16 from './assets/image-9x16.jpg';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
  COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/i,
  MONEY_REGEX = /^[0-9]*\.?[0-9]?[0-9]?$/;

export const TEL_REGEX = /^tel:\+1\d{10}$/;
export const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/i;

export function averageAdjoyabilityForCampaigns(campaigns){
  const numerator = campaigns.reduce((total,c) => total + c.adjoyability * c.completions, 0),
    denominator = campaigns.reduce((total,c) => total + c.completions, 0);

  return (numerator / denominator) || 2;
}

export function timeForEngagements(engagements){
  return engagements.reduce((total,engagement) => {
    if(engagement.kind === 'ratable') return total + 30;
    if(engagement.kind === 'qa' || engagement.kind === 'puzzle') return total + 10;
    return total + 5;
  },0);
}

export function humanReadableTime(timeInSeconds){
  if(isNaN(timeInSeconds)){
    return '0 sec';
  } else if(timeInSeconds > 60){
    return `${Math.floor(timeInSeconds / 60)} min ${timeInSeconds % 60} sec`;
  } else {
    return `${timeInSeconds} sec`
  }
}

export function humanReadableList(list,conjunction = 'and'){
  if(list.length === 1){
    return list[0];
  } else if(list.length === 2){
    return list.join(` ${conjunction} `);
  } else {
    return list.slice(0,-1).join(', ') + `, ${conjunction} `+ list[list.length - 1]
  }
}

export function formatDateRange(startDate='',endDate='',seperator='-'){
  let startDateMoment = moment(startDate),
    endDateMoment = moment(endDate)


  if(startDateMoment.isValid() && endDateMoment.isValid()){
    return `${moment(startDate).format('MMM D, YYYY')} ${seperator} ${moment(endDate).format('MMM D, YYYY')}`
  } else if( startDateMoment.isValid()){
    return `${moment(startDate).format('MMM D, YYYY')}`;
  } else {
    return ''
  }
}

export function emojiUrlForRating(rating){
  if(rating < 0.5) return require('./assets/unhappiest-new.svg');
  if(rating < 1.5) return require('./assets/unhappy-new.svg');
  if(rating < 2.5) return require('./assets/meh-new.svg');
  if(rating < 3.5) return require('./assets/happy-new.svg');
  return require('./assets/happiest-new.svg');
}

export function formatStatus(status){
  if(status === 'complete') return 'Completed';
  if(status === 'draft') return 'Draft';
  if(status === 'pendingAgency') return 'Pending Admin Approval';
  if(status === 'pendingAdjoy') return 'Pending Dabbl Approval';
  if(status === 'suspended') return 'Paused';
  if(status === 'outOfFunds') return 'Budget Met';

  return capitalize(status);
}

export function formatEngagementKind(kind){
  if(kind === 'choose') return 'Multiple Choice'
  if(kind === 'ratable') return 'Video';
  if(kind === 'qa') return 'Trivia';
  if(kind === 'this_or_that') return 'This or That';

  return capitalize(kind);
}

export function formatPaymentMethod(paymentMethod){
  if(!paymentMethod) return '';

  if(paymentMethod.kind === 'invoice') return 'Invoice';
  if(paymentMethod.kind === 'adjoy') return 'Dabbl Pays';
  if(paymentMethod.kind === 'card') return `Card ending in ${paymentMethod.last4}`;
  return '';
}

export function formatPromoCode(promoCode){
  if(!promoCode || !promoCode.code) return '';
  return `${promoCode.code} ($${(promoCode.amount || 0).toLocaleString()} off)`
}

export function uploadImageWithCrop(file,crop){
  const queryString = `w=${crop.width}&h=${crop.height}&x=${crop.x}&y=${crop.y}&ar=${crop.aspectRatio}`,
    formData = new FormData();

  formData.append('file',file);

  return fetch(`${IMAGE_UPLOADER_URL}?${queryString}`,{
    method:'POST',
    body:formData,
    headers:{
      'accept':'application/json'
    }
  })
    .then((res) => {
      if(!res.ok){
        console.log('GOT ERROR',res);
        throw new Error(res.statusText)
      } else {
        return res;
      }
    })
    .then((res) => res.json())
}


export function uploadVideo(file){
  const formData = new FormData();

  formData.append('file',file);

  return fetch(`${VIDEO_UPLOADER_URL}`,{
    method:'POST',
    body:formData,
    headers:{
      'accept':'application/json'
    }
  })
    .then((res) => {
      if(!res.ok){
        throw new Error(res.statusText)
      } else {
        return res;
      }
    })
    .then((res) => res.json())
}

export function alertSalesTeam(userName,userEmail,userRole,agencyName){
  const host = 'https://us-central1-adjoy-main.cloudfunctions.net',
    path ='notifySalesChannelWhenUserSignsIn';

    return fetch(`${host}/${path}`,{
      method:'POST',
      headers:{
        'Content-Type':'application/json'
      },
      body:JSON.stringify({
        user:userName,
        email:userEmail,
        agency:agencyName,
        role:userRole
      })
    });
}

export function alertApprovers(campaign,url,salesId,account,brand,startDate){
  const host = 'https://us-central1-adjoy-main.cloudfunctions.net',
    path ='alertApprovers',
    body = JSON.stringify({
      campaign:campaign,
      url:url,
      salesId:salesId,
      account:account,
      brand:brand,
      startDate:moment(startDate).format('MMM D, YYYY')
    });

    console.log('SENDING BODY ->',body);

    return fetch(`${host}/${path}`,{
      method:'POST',
      headers:{
        'Content-Type':'application/json'
      },
      body:body
    });
}

export function checkStatusOfUser(email){
  return fetch(`${API_URL}/pub/v1/user/state?email=${email}`,{
    method:'GET',
    headers:{
      'Accept':'application/json',
      'Content-Type':'application/json'
    }
  })
  .then((res) => {
    if(res.status === 404){
      return Promise.resolve({
        data:{
          active:false,
          emailVerified:false
        }
      });
    } else {
      return res.json();
    }
  })
  .then((res) => res.data);
}

export function generateGUID(){
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c){
    // eslint-disable-next-line
    var r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8);
    return v.toString(16);
  });
}

export function isEngagementValid(engagement){
  if(engagement.kind === 'this_or_that'){
    return !!engagement.firstImageId && !!engagement.secondImageId;

  } else if(engagement.kind === 'ratable'){
    return !!engagement.videoId && (engagement.text || '').length > 0;

  } else if(engagement.kind ==='choose'){
    const hasImage = !!engagement.firstImageId,
      enoughAnswers = (engagement.options || []).length >= 2,
      noBlankAnswers = (engagement.options || []).every((o) => o.answer.length > 0),
      noBlankQuestion = (engagement.text || '').length > 0;

    return hasImage && enoughAnswers && noBlankQuestion && noBlankAnswers;

  } else if(engagement.kind === 'qa'){
    const hasImages = !!engagement.firstImageId && !!engagement.secondImageId,
      enoughAnswers = (engagement.options || []).length >= 2,
      noBlankAnswers = (engagement.options || []).every((o) => o.answer.length > 0),
      noBlankQuestion = (engagement.text || '').length > 0,
      hasCorrectOption = !!engagement.correctAnswerId && (engagement.correctAnswerText || '').length > 0;

    return hasImages && enoughAnswers && noBlankQuestion && noBlankAnswers && hasCorrectOption;
  } else if(engagement.kind ==='puzzle'){
    return !!engagement.firstImageId && (engagement.text || '').length > 0
  } else {
    return true;
  }
}

export function isEmpty(val){
  return !val || val.length === 0;
}

export function isCoverValid(campaign){
  return campaign.feedImageURL && campaign.feedImageURL.length > 0
}

export function isConclusionValid(campaign){
  const textIsValid = campaign.completionText && campaign.completionText.replace(/<\/?[^>]+(>|$)/g, "").trim().length > 0,
    imageIsValid = campaign.completionImageURL && campaign.completionImageURL.length > 0;

  let ctaIsValid = true;
  if(campaign.useCTA){
    if(campaign.useAppLink) {
      if ((!campaign.iosLink || campaign.iosLink.length == 0) && (!campaign.androidLink || campaign.androidLink.length == 0)) {
        ctaIsValid = false;
      }
    } else {
      if(!campaign.ctaLink || campaign.ctaLink.length === 0){
        ctaIsValid = false;
      } else if(!(URL_REGEX.test(campaign.ctaLink) || TEL_REGEX.test(campaign.ctaLink))){
        ctaIsValid = false;
      }
    }
  }

  return textIsValid && imageIsValid && ctaIsValid;
}

export function urlRoot(pathname){
  const parts = pathname.split('/');

  if(parts[1] === 'impersonate'){
    return parts.slice(0,3).join('/');
  } else {
    return parts.slice(0,2).join('/');
  }
}

export function parseQueryString(queryStr){
  if(queryStr === '') return {};
  if(queryStr[0] === '?') queryStr = queryStr.substring(1);

  return queryStr.split('&').reduce((obj,str) => {
    const key = decodeURIComponent(str.split('=')[0]),
      val = decodeURIComponent(str.split('=')[1]);

    obj[key] = val;

    return obj;
  },{});
}

export function asObject(array,idAttribute='id'){
  return array.reduce((obj,item) => Object.assign({},obj,{[item[idAttribute]]:item}),{});
}

export function batchUpdateAsObjects(array){
  const updateObject = {};
  array.forEach((item) => updateObject[item.collection] = asObject(item.instances));
  return updateObject
}

export function divideArrayIntoNGroups(array,n){
  const dividedArray = [];

  array.forEach((item,itemIndex) => {
    const groupIndex = itemIndex % n;
    if(!dividedArray[groupIndex]) dividedArray[groupIndex] = [];
    dividedArray[groupIndex].push(item);
  });

  return dividedArray;
}

export function shortenTextIfNeeded(text='',maxChars=1000){
  const tokens = text.split(' ');

  let shortenedText = '';

  for(let i = 0; i < tokens.length; i++){
    if((shortenedText + ' '+tokens[i]).length > maxChars){
      shortenedText = shortenedText + '...';
      break;
    } else {
      shortenedText = shortenedText + ' '+tokens[i];
    }
  }

  return shortenedText;
}

export function cutOffLongTextIfNeeded(text='',maxChars=100){
  if(!text) return '';
  if(text.length > maxChars-3){
    return text.slice(0,maxChars - 3) + '...';
  } else {
    return text;
  }
}

export function nameForEngagementKind(kind){
  if(kind === 'choose') return 'Multiple Choice';
  if(kind === 'ratable') return 'Video';
  if(kind === 'this_or_that') return 'This or That';

  return capitalize(kind);
}

export function hideChatBox(){
  const element = document.getElementById('lc_chat_layout');
  if (window.driftt) {
    window.driftt.hide()
  }
  if(element){
    element.style.display = 'none';
  }
}

export function recursivelyLoadAllPages(loadFunc,pageSize=1000){
  var pageNumber = 0;

  const loadPage = () => {
    return loadFunc(pageNumber,pageSize)
      .then((res) => {
        if(res.hasNextPage){
          pageNumber = pageNumber + 1;
          return loadPage();
        } else {
          return Promise.resolve();
        }
      });
  }

  return loadPage();
}

export function firstNonZeroOrOne(comparisons){
  return find(comparisons,(c) => c !== 0) || 1;
}

export function daysSinceEpoch(timestamp){
  return Math.floor(timestamp / (24 * 60 * 60 * 1000))
}

export function getScrollTop(){
  return (window.pageYOffset !== undefined) ? window.pageYOffset : (document.documentElement || document.body.parentNode || document.body).scrollTop;
}

export function getWindowHeight(){
  return window.innerHeight;
}

export function roundUpToNearestCent(amount){
  return Math.ceil(parseFloat((amount * 100).toPrecision(12))) / 100
}

export function roundUpToNearestTensOfCent(amount){
  return Math.ceil(parseFloat((amount * 1000).toPrecision(12))) / 1000
}

export function roundToNearestCent(amount){
  return Math.round(amount * 100)/100
}

export function costPerCompletion(campaign,engagements){
  if(campaign.useCostPerCompletion){
    return campaign.costPerCompletion;
  } else {
    return engagements.reduce((cost,e) => {
      if(e.kind === 'choose') return cost + (campaign.chooseCost || 0);
      if(e.kind === 'this_or_that') return cost + (campaign.thisOrThatCost || 0);
      if(e.kind === 'ratable') return cost + (campaign.ratableCost || 0);
      if(e.kind === 'qa') return cost + (campaign.qaCost || 0);
      if(e.kind === 'puzzle') return cost + (campaign.puzzleCost || 0);
      return cost;
    },0);
  }
}

export function roundNumbersAddingUpTo100(unroundedArray){
  const roundedArrayWithIndexes = unroundedArray.map((a,i) => ({
    original:a,
    rounded:Math.floor(a),
    mantissa:a - Math.floor(a),
    index:i
  }));

  var toBeDistributed = 100 - roundedArrayWithIndexes.reduce((sum,a) => sum + a.rounded,0)

  const fudgedArray = roundedArrayWithIndexes
    .sort((a,b) => b.mantissa - a.mantissa)
    .map((a) => {
      if(toBeDistributed === 0 || a.rounded === 0){
        return {...a, fudged: a.rounded}
      } else if(toBeDistributed > 0){
        toBeDistributed = toBeDistributed - 1;
        return {...a, fudged: a.rounded + 1}
      } else {
        return {...a, fudged: a.rounded}
      }
    })

   const fudgedSortedArray = fudgedArray
    .sort((a,b) => a.index - b.index)
    .map((a) => a.fudged)

  //TODO SORT BY MANTISSA


  return fudgedSortedArray;
}

export function validationMessagesFromStripeError(stripeError){
  const validationMessages = {};

  if(stripeError.code === 'incomplete_number' || stripeError.code === 'invalid_number'){
    validationMessages['number'] = stripeError.message;
  }

  if(stripeError.code === 'invalid_expiry_month' || stripeError.code === 'invalid_expiry_year' || stripeError.code === 'expired_card'){
    validationMessages['expiry'] = stripeError.message;
  }

  if(stripeError.code === 'invalid_cvc' || stripeError.code === 'incorrect_cvc'){
    validationMessages['cvc'] = stripeError.message;
  }

  if(stripeError.code === 'incorrect_zip'){
    validationMessages['zip'] = stripeError.message;
  }

  return validationMessages;
}

export function formatIncompleteEngagements(ids=[]){
  if(ids.length === 0) return '';
  if(ids.length === 1) return `Activity ${ids[0] + 1} is incomplete`;
  return `Activities ${humanReadableList(ids.map((id) => id + 1))} are incomplete`;
}

export function countsToRoundedPercents(counts){
  const total = counts.reduce((t,c) => t + c, 0),
    unroundedPercents = counts.map((c) => (c/total) * 100);

    return roundNumbersAddingUpTo100(unroundedPercents);
}


export function delayPromiseChain(time,val){
  return new Promise((res) => {
    if(time <= 0){
      return res(val)
    } else {
      window.setTimeout(() => res(val),time)
    };
  })
}

export function dayIsInPast(date){
  const dateMoment = moment(date),
    todayMoment = moment();

  if(!date || date.length === 0){
    return false;
  } else {
    return dateMoment.diff(todayMoment,'days') < 0
  }
}

export function getPreviewLinkForCampaignWithPreviewId(previewId){
  if(ACTIVE_PROFILE === 'test' || ACTIVE_PROFILE === 'local'){
    return `https://test-webapp.getdabbl.com/preview/${previewId}`;
  } else if(ACTIVE_PROFILE === 'demo'){
    return `https://demo-webapp.getdabbl.com/preview/${previewId}`;
  } else if(ACTIVE_PROFILE === 'stage'){
    return `https://stage-webapp.getdabbl.com/preview/${previewId}`;
  } else {
    return `https://webapp.getdabbl.com/preview/${previewId}`;
  }
}

export function getWelcomeLinkForUser(user){
  if(ACTIVE_PROFILE === 'test' || ACTIVE_PROFILE === 'local'){
    return `https://test-create.getdabbl.com/welcome?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  } else if(ACTIVE_PROFILE === 'demo'){
    return `https://demo-create.getdabbl.com/welcome?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  } else if(ACTIVE_PROFILE === 'stage'){
    return `https://stage-create.getdabbl.com/welcome?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  } else {
    return `https://create.getdabbl.com/welcome?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  }
}

export function getInviteLinkForUserToAgency(user,agency){
  if(ACTIVE_PROFILE === 'test' || ACTIVE_PROFILE === 'local'){
    return `https://test-create.getdabbl.com/invite?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}&agencyName=${encodeURIComponent(agency.name)}&companyWebsiteURL=${encodeURIComponent(agency.websiteUrl || '')}`;
  } else if(ACTIVE_PROFILE === 'demo'){
    return `https://demo-create.getdabbl.com/invite?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}&agencyName=${encodeURIComponent(agency.name)}&companyWebsiteURL=${encodeURIComponent(agency.websiteUrl || '')}`;
  } else if(ACTIVE_PROFILE === 'stage'){
    return `https://stage-create.getdabbl.com/invite?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}&agencyName=${encodeURIComponent(agency.name)}&companyWebsiteURL=${encodeURIComponent(agency.websiteUrl || '')}`;
  } else {
    return `https://create.getdabbl.com/invite?userId=${encodeURIComponent(user.id)}&email=${encodeURIComponent(user.email)}&token=${encodeURIComponent(user.emailVerificationToken)}&agencyName=${encodeURIComponent(agency.name)}&companyWebsiteURL=${encodeURIComponent(agency.websiteUrl || '')}`;
  }
}

export function getResetLinkForUser(user){
  if(ACTIVE_PROFILE === 'test' || ACTIVE_PROFILE === 'local'){
    return `https://test-create.getdabbl.com/set-password&userId=${encodeURIComponent(user.id)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  } else if(ACTIVE_PROFILE === 'demo'){
    return `https://demo-create.getdabbl.com/set-password&userId=${encodeURIComponent(user.id)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  } else if(ACTIVE_PROFILE === 'stage'){
    return `https://stage-create.getdabbl.com/set-password&userId=${encodeURIComponent(user.id)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  } else {
    return `https://create.getdabbl.com/set-password&userId=${encodeURIComponent(user.id)}&token=${encodeURIComponent(user.emailVerificationToken)}`;
  }
}

export function getApprovalLinkForCampaign(campaign){
  if(ACTIVE_PROFILE === 'test' || ACTIVE_PROFILE === 'local'){
    return `https://test-create.getdabbl.com/superadmin/experiences/${campaign.id}/review`;
  } else if(ACTIVE_PROFILE === 'demo'){
    return `https://demo-create.getdabbl.com/superadmin/experiences/${campaign.id}/review`;
  } else if(ACTIVE_PROFILE === 'stage'){
    return `https://stage-create.getdabbl.com/superadmin/experiences/${campaign.id}/review`;
  } else {
    return `https://create.getdabbl.com/superadmin/experiences/${campaign.id}/review`;
  }
}


export function isGUID(potentialGuid){
  return GUID_REGEX.test(potentialGuid);
}

export function formatPhoneNumber(phone){
  return `${phone[0]} (${phone.slice(1,4)}) ${phone.slice(4,7)}-${phone.slice(7)}`;
}

export function textContains(text,search=''){
  return (text || '').toLowerCase().indexOf(search.toLowerCase()) > -1;
}

export function alphabeticallySort(a,b){
  return ((a || '').toLowerCase()).localeCompare((b || '').toLowerCase());
}

export function statusSort(a,b){
  const statusesInOrder = ['draft','pendingAgency','pendingAdjoy','ready','active','suspended','outOfFunds','complete'];

  return statusesInOrder.indexOf(a) - statusesInOrder.indexOf(b);
}

export function countSort(a,b){
  if(a === null && b === null) return 0;
  if(b === null) return -1;
  if(a === null) return 1;
  if(a !== null && b!== null) return b - a;
  return b - a;
}

export function stripBuildFromTag(tag){
  if(tag.indexOf('-') < 0) return tag;
  return tag.split('-')[1];
}


export function geoFilterSort(a,b){
  if(a.kind === b.kind){
    return (a.name || '').localeCompare(b.name || '')
  } else if(a.kind === 'state'){
    return -1
  } else {
    return 1
  }
}

export function buildNestedList(items){
  for(let i = 0; i < items.length; i++){
    let item = items[i];

    item.children = [];

    if(item.parent && item.parent.length > 0){
      for(let j = 0; j < item.parent.length; j++){
        let parentItem = items.find((i) => i.code === item.parent[j] || i.id === item.parent[j]);

        if(parentItem && parentItem.children){
          parentItem.children.push(item);
        } else if(parentItem){
          parentItem.children = [item];
        }
      }
    }

  }

  return items
    .filter((item) => item.name && item.name.length > 0)
    .filter((item) => !item.parent || item.parent.length === 0);
}


export function formatGeofilterSelectOptions(items){
  let finalItems = [];

  for(let i=0; i < items.length; i++){
    let item = items[i];

    finalItems.push({
      value:item.code,
      label:`${toTitleCase(item.name)} (State)`,
      data:item
    });

    if(item.children){
      let finalChildren = [];

      for(let j=0; j <item.children.length; j++){
        let child = item.children[j];

        finalChildren.push({
          value:child.code,
          label:toTitleCase(child.name.toLowerCase()),
          data:child
        });

      }

      finalItems.push({
        options:finalChildren,
        label:`${toTitleCase(item.name)} DMAs`,
        data:{}
      });
    }
  }

  return finalItems;
}


export function toTitleCase(string){
  let smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|v.?|vs.?|via)$/i
  let alphanumericPattern = /([A-Za-z0-9\u00C0-\u00FF])/
  let wordSeparators = /([ :–—-])/

  return string.split(wordSeparators)
    .map(function (current, index, array) {
      if (
        /* Check for small words */
        current.search(smallWords) > -1 &&
        /* Skip first and last word */
        index !== 0 &&
        index !== array.length - 1 &&
        /* Ignore title end and subtitle start */
        array[index - 3] !== ':' &&
        array[index + 1] !== ':' &&
        /* Ignore small words that start a hyphenated phrase */
        (array[index + 1] !== '-' ||
          (array[index - 1] === '-' && array[index + 1] === '-'))
      ) {
        return current.toLowerCase()
      }

      /* Ignore intentional capitalization */
      if (current.substr(1).search(/[A-Z]|\../) > -1) {
        return current
      }

      /* Ignore URLs */
      if (array[index + 1] === ':' && array[index + 2] !== '') {
        return current
      }

      /* Capitalize the first letter */
      return current.replace(alphanumericPattern, function (match) {
        return match.toUpperCase()
      })
    })
    .join('')
    .replace(new RegExp("-", "g"),' - ')
}


export function engagementOrDefaults(originalEngagement){
  const engagement = Object.assign({},originalEngagement);

  //TODO, NO DEFAULTS IF ANYTHING FILTERED OUT

  if(engagement.kind === 'choose'){
    if(!engagement.firstImageURL){
      engagement.firstImageURL = Placeholder4x3;
    }

    if((!engagement.options || engagement.options.length === 0) && (!engagement.text || engagement.text ==='')){
      engagement.text = 'Do you like our latest flavor?';
      engagement.options = [
        {answer: 'Yes!', id: '11'},
        {answer: 'Not really...', id: '12'},
        {answer: "It's not my thing", id: '13'},
        {answer: 'I hate it!', id: '14'},
      ];
    }
  } else if(engagement.kind === 'this_or_that'){
    if(!engagement.firstImageURL){
      engagement.firstImageURL = Placeholder4x3;
    }

    if(!engagement.secondImageURL){
      engagement.secondImageURL = Placeholder4x3;
    }

  } else if(engagement.kind === 'ratable' && (!engagement.text || engagement.text ==='') && !engagement.videoURL){
    engagement.text = 'Check out our latest offering';

  } else if(engagement.kind === 'puzzle'){
      if(!engagement.firstImageURL){
        engagement.firstImageURL = Placeholder9x16;
      }
  } else if(engagement.kind === 'qa'){
    if(!engagement.firstImageURL){
      engagement.firstImageURL = Placeholder4x3;
    }

    if(!engagement.secondImageURL){
      engagement.secondImageURL = Placeholder4x3;
    }

    if((!engagement.options || engagement.options.length === 0) && (!engagement.text || engagement.text ==='') ){
      engagement.text = 'Which fruit has the most Vitamin C?';
      engagement.correctAnswerId = '2';
      engagement.correctAnswerText = 'Vitamin C protects against heart disease, aids in absorption of iron, and decreases bad cholesterol and triglycerides. It may also lessen symptoms of a common cold & support healthy immune function.';
      engagement.options = [
        {answer: 'Apple', id: '1'},
        {answer: 'Orange', id: '2'},
        {answer: 'Grapefruit', id: '3'},
        {answer: 'Tangerine', id: '4'},
      ];
    }
  }



  return engagement
}

export function titleForEngagement(e){
  if(e.kind === 'this_or_that'){
    return `${e.firstImageTitle} or ${e.secondImageTitle}?`
  } else {
    return e.text;
  }
}

export function typeOfContraints(constraints){
  if(!constraints || constraints.length === 0) return {name: 'all', exclude: false};
  if(constraints[0].filterType === 0) return {name: 'completed', exclude: constraints[0].exclude };
  if(constraints[0].filterType === 1) return {name: 'response', exclude: constraints[0].exclude };
  if(constraints[0].filterType === 2) return {name: 'user', exclude: false };
  if(constraints[0].filterType === 3) return {name: 'user', exclude: true };
  return '';
}

export function constraintDisplayName(activeCampaign) {
  console.log('In constraintDisplayName with ', activeCampaign);

  const constraintType = typeOfContraints(activeCampaign.constraints);

  if(!activeCampaign || (activeCampaign && !activeCampaign.constraints)){
    return 'Loading...';
  } else if(constraintType.name === 'all' || constraintType === ''){
    return 'None';
  } else if(constraintType.name === 'completed'){
    return (constraintType.exclude === true ? 'Exclude' : 'Include') + ' Completed Experience';
  } else if(constraintType === 'user') {
    return (constraintType.exclude === true ? 'Exclude' : 'Include') + ' Selected Users';
  } else {
    return 'Selected Response(s)';
  }
}



// WEBPACK FOOTER //
// ./src/shared/utils.js